<template>
  <MetaHead :title="wt('title')" description=" " />
  <VersionUpdate
    v-if="officeBase.availableUpdates.value?.length"
    :versionUpdates="officeBase.availableUpdates.value"
    @onIgnore="officeBase.ignoreVersion"
    @onUpdate="officeBase.updateVersion" />
  <template v-if="officeBase.officeMeta.value.canEdit">
    <div class="core-wrapper relative">
      <StepsIndicator class="no-header" />

      <!-- TODO: hide, if it's not a CH project -->
      <BaseSection id="location" :nextStep="true" @onNextStepClick="officeBase.onNextStepClick">
        <h2 class="text-xl">{{ wt('location', 'title') }}: {{ officeBase.officeName.value }}</h2>
        <HelpText slug="office_location"></HelpText>
        <SwissMapForm v-model="formData" v-if="formData" />
      </BaseSection>

      <BaseSection id="structure" :nextStep="true" @onNextStepClick="officeBase.onNextStepClick">
        <h2 class="text-xl">{{ wt('structure', 'title') }}</h2>
        <HelpText slug="office_structure"></HelpText>
        <OfficeStructureForm v-model="formData" v-if="formData" />
      </BaseSection>

      <BaseSection id="order_structure" :nextStep="true" @onNextStepClick="officeBase.onNextStepClick">
        <h2 class="text-xl">{{ wt('orderStructure', 'title') }}</h2>
        <HelpText slug="office_order_structure"></HelpText>
        <OfficeOrderStructureForm v-model="formData" v-if="formData" />
      </BaseSection>

      <BaseSection id="work_reserve" :nextStep="true" @onNextStepClick="officeBase.onNextStepClick">
        <h2 class="text-xl">{{ wt('workReserve', 'title') }}</h2>
        <HelpText slug="office_work_reserve"></HelpText>
        <OfficeWorkRerserveForm v-model="formData" v-if="formData" />
      </BaseSection>

      <BaseSection id="risk" :nextStep="true" @onNextStepClick="officeBase.onNextStepClick">
        <h2 class="text-xl">{{ wt('risk', 'title') }}</h2>
        <HelpText slug="office_risk"></HelpText>
        <OfficePlanningRiskForm v-model="formData" v-if="formData" />
      </BaseSection>

      <BaseSection id="summary">
        <h2 class="text-xl">{{ wt('summary', 'title') }}</h2>
        <HelpText slug="office_summary"></HelpText>
        <OfficeSummaryForm v-model="formData" v-if="formData" />
        <p v-if="officeBase.office?.value?.version" class="mt-16 text-right text-sm text-neutral-550">
          Version {{ officeBase.office.value.version }}
        </p>
      </BaseSection>
    </div>

    <ProjectResults :projectName="officeBase.officeMeta.value.title" @onSaveProject="onSaveOffice"></ProjectResults>
  </template>

  <template v-else>
    <div class="core-wrapper relative">
      <BaseSection>
        <h2 class="text-xl">{{ wt('summary', 'title') }}: {{ officeBase.officeName.value }}</h2>
        <HelpText slug="office_summary"></HelpText>
        <OfficeSummaryForm v-model="formData" v-if="formData" />
      </BaseSection>
    </div>
  </template>
</template>

<script setup lang="ts">
import { onMounted, ref, type Ref, watch } from 'vue'
import { useWidgetText } from '@/helpers/useWidgetText'
import type { NestedObject } from '@/types'
import officePageBase, { type OfficeValidationConfig } from '@/base/officePageBase'
import StepsIndicator from '@/components/partials/navigation/StepsIndicator.vue'
import BaseSection from '@/components/partials/layout/BaseSection.vue'
import HelpText from '@/components/partials/display/HelpText.vue'
import MetaHead from '@/components/partials/display/MetaHead.vue'
import SwissMapForm from '@/components/project/general/SwissMapForm.vue'
import { onBeforeRouteLeave } from 'vue-router'
import OfficeStructureForm from '@/components/office/OfficeStructureForm.vue'
import OfficeOrderStructureForm from '@/components/office/OfficeOrderStructureForm.vue'
import OfficeWorkRerserveForm from '@/components/office/OfficeWorkRerserveForm.vue'
import OfficePlanningRiskForm from '@/components/office/OfficePlanningRiskForm.vue'
import OfficeSummaryForm from '@/components/office/OfficeSummaryForm.vue'
import ProjectResults from '@/components/partials/display/ProjectResults.vue'
import VersionUpdate from '@/components/partials/display/VersionUpdate.vue'
import { requiredValue, requiredValueInCountry } from '@/validation/field-validators'

// Validation config for office page
const validationConfig: OfficeValidationConfig = {
  schema: {
    canton: requiredValue,
    foundingYear: requiredValueInCountry,
    competitions: requiredValue,
    followUpJobsFromCompetitions: requiredValue,
    generalPlanningMandates: requiredValue,
    planningCommunityMandates: requiredValue,
    subPlanningMandates: requiredValue,
    publicClients: requiredValue,
    professionalPrivates: requiredValue,
    orderBacklogWorkReserve: requiredValue,
    planningRisk: requiredValue,
  },
  fieldSections: {
    canton: '#location',
    foundingYear: '#structure',
    competitions: '#order_structure',
    followUpJobsFromCompetitions: '#order_structure',
    generalPlanningMandates: '#order_structure',
    planningCommunityMandates: '#order_structure',
    subPlanningMandates: '#order_structure',
    publicClients: '#order_structure',
    professionalPrivates: '#order_structure',
    orderBacklogWorkReserve: '#work_reserve',
    planningRisk: '#risk',
  },
  sectionErrors: {
    '#location': 'office.sections.location.error.default',
    '#structure': 'office.sections.structure.error.default',
    '#order_structure': 'office.sections.order_structure.error.default',
    '#work_reserve': 'office.sections.work_reserve.error.default',
    '#risk': 'office.sections.risk.error.default',
  },
}

const { wt } = useWidgetText('widget-texts-office')
const formData: Ref<NestedObject | undefined> = ref()
const officeBase = officePageBase.useOfficePageBase(validationConfig)
officeBase.initCalculator()

function initForm(): void {
  formData.value = officeBase.office.value?.data as NestedObject
  officeBase.validator.initForm(formData.value, officeBase.validationSchema)
}

async function onSaveOffice(): Promise<void> {
  if (!formData.value) {
    return
  }
  const success = await officeBase.saveOffice(formData.value)
  if (success) {
    initForm()
  }
}

onMounted(() => {
  initForm()
})

onBeforeRouteLeave(async (to, from, next) => {
  await officeBase.checkFormIsDirty(next, { data: formData.value })
})

watch(
  formData,
  () => {
    officeBase.validator.onFormValuesChange(formData.value as NestedObject)
    officeBase.calculator.setOffice(formData.value as NestedObject)
  },
  { deep: true, immediate: false }
)
</script>
